// Templates outside of work: sport, cooking and everyday life. Interval
// workouts repeat the same pattern many times, so they are generated; the
// label of every variant states its total duration (checked in
// examples.test.js).

function hhmmss(totalSeconds) {
  const pad = (n) => String(n).padStart(2, "0");
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

const card = (topic, seconds, extra = {}) => ({
  topic,
  time: hhmmss(seconds),
  ...extra,
});

const MIN = 60;

function formatTotal(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours) {
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes) {
    return seconds ? `${minutes}m ${seconds}s` : `${minutes} min`;
  }
  return `${seconds}s`;
}

// A variant whose label ends in its total duration, e.g. "M (5m 30s)".
function variant(name, agenda) {
  const total = agenda.reduce((sum, item) => {
    const [h, m, s] = item.time.split(":").map(Number);
    return sum + h * 3600 + m * 60 + s;
  }, 0);
  return { label: `${name} (${formatTotal(total)})`, agenda };
}

const warmUp = (seconds = 5 * MIN) =>
  card("Warm-up", seconds, {
    type: "workout",
    note: "March on the spot, arm circles, easy squats",
  });
const coolDown = (seconds = 5 * MIN) =>
  card("Cool-down", seconds, { type: "break", note: "Walk it out, stretch" });

// rounds x (work, rest), cycling through the exercises. The rest after
// the last round is left out.
function intervals(rounds, workSeconds, restSeconds, exercises) {
  const cards = [];
  for (let round = 0; round < rounds; round++) {
    const exercise = exercises[round % exercises.length];
    cards.push(
      card(`${exercise} ${round + 1}/${rounds}`, workSeconds, {
        type: "workout",
      }),
    );
    if (round < rounds - 1) {
      cards.push(card("Rest", restSeconds, { type: "break" }));
    }
  }
  return cards;
}

const tabataBlock = (exercise) => intervals(8, 20, 10, [exercise]);

const CIRCUIT = [
  ["Squats", "Feet shoulder-width, sit back"],
  ["Push-ups", "On your knees is fine"],
  ["Lunges", "Alternate legs"],
  ["Plank", "Straight line from head to heels"],
  ["Burpees", "Step back instead of jumping if needed"],
  ["Mountain Climbers", "Keep the hips low"],
];

function circuit(rounds) {
  const cards = [warmUp()];
  for (let round = 1; round <= rounds; round++) {
    CIRCUIT.forEach(([exercise, note], index) => {
      cards.push(
        card(`${exercise} (round ${round})`, 45, { type: "workout", note }),
      );
      if (index < CIRCUIT.length - 1) {
        cards.push(card("Rest", 15, { type: "break" }));
      }
    });
    if (round < rounds) {
      cards.push(card("Round break", MIN, { type: "break" }));
    }
  }
  cards.push(coolDown());
  return cards;
}

function runWalk(rounds, runSeconds, walkSeconds) {
  const cards = [card("Warm-up walk", 5 * MIN, { type: "break" })];
  for (let round = 1; round <= rounds; round++) {
    cards.push(
      card(`Run ${round}/${rounds}`, runSeconds, {
        type: "workout",
        note: "Easy pace - you should still be able to talk",
      }),
    );
    cards.push(card("Walk", walkSeconds, { type: "break" }));
  }
  cards.push(card("Cool-down walk", 5 * MIN, { type: "break" }));
  return cards;
}

function strength(exercises, sets) {
  const cards = [warmUp()];
  exercises.forEach((exercise, exerciseIndex) => {
    for (let set = 1; set <= sets; set++) {
      cards.push(
        card(`${exercise} - set ${set}/${sets}`, MIN, {
          type: "workout",
          note: "8-12 controlled reps",
        }),
      );
      const last = exerciseIndex === exercises.length - 1 && set === sets;
      if (!last) {
        cards.push(card("Rest", 90, { type: "break" }));
      }
    }
  });
  return cards;
}

const STRETCHES = [
  ["Neck", "Ear to shoulder, both sides"],
  ["Shoulders", "Arm across the chest, both sides"],
  ["Chest", "Hands clasped behind the back"],
  ["Hamstrings left", "Straight leg, reach for the toes"],
  ["Hamstrings right", "Straight leg, reach for the toes"],
  ["Quads left", "Heel to the bottom"],
  ["Quads right", "Heel to the bottom"],
  ["Hips", "Deep lunge, sink the hips"],
  ["Calves", "Against a wall, heel down"],
  ["Lower Back", "Lying down, knees to the chest"],
];

export const sportExamples = [
  {
    event: "Tabata",
    description:
      "8 rounds of 20 seconds all-out and 10 seconds rest per block. One block is a quick 4-minute burst.",
    variants: [
      variant("1 block", tabataBlock("Squats")),
      variant("4 blocks", [
        warmUp(),
        ...tabataBlock("Squats"),
        card("Block break", MIN, { type: "break" }),
        ...tabataBlock("Push-ups"),
        card("Block break", MIN, { type: "break" }),
        ...tabataBlock("Mountain Climbers"),
        card("Block break", MIN, { type: "break" }),
        ...tabataBlock("Burpees"),
        coolDown(),
      ]),
    ],
  },
  {
    event: "HIIT",
    description:
      "High-intensity intervals with rest in between, rotating through five bodyweight exercises.",
    variants: [
      variant("30/30, 10 rounds", [
        warmUp(),
        ...intervals(10, 30, 30, [
          "Jumping Jacks",
          "Squats",
          "Push-ups",
          "Lunges",
          "High Knees",
        ]),
        coolDown(5 * MIN + 30),
      ]),
      variant("40/20, 15 rounds", [
        warmUp(),
        ...intervals(15, 40, 20, [
          "Jumping Jacks",
          "Squats",
          "Push-ups",
          "Lunges",
          "High Knees",
        ]),
        coolDown(5 * MIN + 20),
      ]),
    ],
  },
  {
    event: "Circuit Training",
    description:
      "Six exercises, 45 seconds each with 15 seconds to switch, a minute's break between rounds.",
    variants: [
      variant("2 rounds", circuit(2)),
      variant("3 rounds", circuit(3)),
      variant("4 rounds", circuit(4)),
    ],
  },
  {
    event: "Strength Training",
    description:
      "Sets of one minute with 90 seconds rest - enough time for 8-12 controlled reps.",
    variants: [
      variant(
        "Full body, 3 sets",
        strength(["Squats", "Push-ups", "Rows", "Glute Bridges"], 3),
      ),
      variant(
        "Upper body, 3 sets",
        strength(["Push-ups", "Rows", "Shoulder Press"], 3),
      ),
    ],
  },
  {
    event: "Run/Walk Intervals",
    description:
      "Alternate running and walking - a gentle way to start running or build up distance.",
    variants: [
      variant("Beginner: run 3, walk 2", runWalk(5, 3 * MIN, 2 * MIN)),
      variant("Next level: run 6, walk 1", runWalk(4, 6 * MIN, MIN)),
    ],
  },
  {
    event: "Stretching",
    description: "A full-body stretch, one minute per position.",
    variants: [
      variant(
        "Full body",
        STRETCHES.map(([topic, note]) => card(topic, MIN, { note })),
      ),
    ],
  },
];

// Egg times for eggs straight from the fridge, lowered into water that is
// already boiling.
const EGG_TIMES = {
  "Soft-boiled": { S: 300, M: 330, L: 360, XL: 390 },
  "Medium-boiled": { S: 390, M: 420, L: 450, XL: 480 },
  "Hard-boiled": { S: 540, M: 570, L: 600, XL: 660 },
};

const EGG_TEXT = {
  "Soft-boiled": "Runny yolk, just set white.",
  "Medium-boiled": "Jammy, creamy yolk.",
  "Hard-boiled": "Firm yolk all the way through.",
};

export const eggExamples = Object.entries(EGG_TIMES).map(
  ([doneness, sizes]) => ({
    event: `${doneness} Eggs`,
    description: `${EGG_TEXT[doneness]} For eggs straight from the fridge, lowered into boiling water - pick the size.`,
    variants: Object.entries(sizes).map(([size, seconds]) =>
      variant(size, [
        card(`${doneness} egg (${size})`, seconds, {
          note: "Lower into boiling water, then rinse under cold water",
        }),
      ]),
    ),
  }),
);

export const cookingExamples = [
  {
    event: "Pasta",
    description: "Cook until almost done, then taste and drain.",
    variants: [
      variant("Spaghetti", [
        card("Cook spaghetti", 8 * MIN, {
          note: "Salt the boiling water first, stir after a minute",
        }),
        card("Taste & drain", MIN, { note: "Keep a cup of pasta water" }),
      ]),
      variant("Penne", [
        card("Cook penne", 10 * MIN, {
          note: "Salt the boiling water first, stir after a minute",
        }),
        card("Taste & drain", MIN, { note: "Keep a cup of pasta water" }),
      ]),
      variant("Fresh pasta", [
        card("Cook fresh pasta", 2 * MIN),
        card("Taste & drain", MIN),
      ]),
    ],
  },
  {
    event: "Rice",
    description: "Bring to a boil, simmer with the lid on, then let it rest.",
    variants: [
      variant("Basmati", [
        card("Bring to a boil", 4 * MIN, {
          note: "1 part rice, 1.5 parts water",
        }),
        card("Simmer covered", 10 * MIN, {
          note: "Lowest heat, don't lift the lid",
        }),
        card("Rest off the heat", 6 * MIN, { type: "break" }),
      ]),
      variant("Long-grain", [
        card("Bring to a boil", 5 * MIN, {
          note: "1 part rice, 2 parts water",
        }),
        card("Simmer covered", 12 * MIN, {
          note: "Lowest heat, don't lift the lid",
        }),
        card("Rest off the heat", 8 * MIN, { type: "break" }),
      ]),
      variant("Brown rice", [
        card("Bring to a boil", 5 * MIN, {
          note: "1 part rice, 2.5 parts water",
        }),
        card("Simmer covered", 30 * MIN, {
          note: "Lowest heat, don't lift the lid",
        }),
        card("Rest off the heat", 10 * MIN, { type: "break" }),
      ]),
    ],
  },
  {
    event: "Steak",
    description:
      "For a 2.5 cm (1 inch) steak in a very hot pan, followed by a rest so the juices settle.",
    variants: [
      ["Rare", 2],
      ["Medium rare", 3],
      ["Medium", 4],
    ].map(([doneness, minutes]) =>
      variant(doneness, [
        card("First side", minutes * MIN, { note: "Don't move it" }),
        card("Flip: second side", minutes * MIN),
        card("Rest", 5 * MIN, { type: "break", note: "On a warm plate" }),
      ]),
    ),
  },
  {
    event: "Tea & Coffee",
    description: "Steeping times for a good cup.",
    variants: [
      variant("Green tea", [
        card("Steep green tea", 150, { note: "Water at 75-80 °C" }),
      ]),
      variant("Black tea", [
        card("Steep black tea", 4 * MIN, { note: "Boiling water" }),
      ]),
      variant("Herbal tea", [
        card("Steep herbal tea", 8 * MIN, { note: "Boiling water, lid on" }),
      ]),
      variant("French press", [
        card("Bloom", 30, {
          note: "Pour a little water, let the coffee swell",
        }),
        card("Steep", 210, { note: "Top up, lid on, then press slowly" }),
      ]),
    ],
  },
];

// Yeast doughs. Rising is waiting time, so those cards are breaks. Ovens
// (and above all a pizza stone) need a long preheat, so it happens during
// the last rise; the card that starts it says so in its topic.
const knead = (note) =>
  card("Mix & knead", 10 * MIN, {
    note: `${note}. Knead until smooth and elastic.`,
  });

const rise = (topic, seconds, note) =>
  card(topic, seconds, { type: "break", note });

// The stone needs a few minutes to heat back up between pizzas; the next
// pizza is stretched and topped while one bakes.
function bakePizzas(count) {
  const cards = [
    card("Stretch & top pizza 1", 4 * MIN, {
      note: "Press from the middle outwards, leave the rim. Semolina on the peel so it slides.",
    }),
  ];
  for (let pizza = 1; pizza <= count; pizza++) {
    cards.push(
      card(`Bake pizza ${pizza}/${count}`, 7 * MIN, {
        note:
          pizza < count
            ? "Slide onto the stone, turn after 4 min. Meanwhile stretch & top the next one."
            : "Slide onto the stone, turn after 4 min.",
      }),
    );
    if (pizza < count) {
      cards.push(
        card("Stone heats back up", 3 * MIN, {
          type: "break",
          note: "Close the oven door - slice and enjoy",
        }),
      );
    }
  }
  return cards;
}

const preheatStone = (seconds) =>
  rise(
    "Proof balls - preheat stone now",
    seconds,
    "Stone on the middle rack, oven at its hottest (250-300 °C). Balls covered so they don't dry out.",
  );

// Dough for 4 pizzas. The yeast is woken up in sugared water first, so
// it's clear it works before the flour goes in.
const pizzaDough = (yeast) => [
  card("Activate the yeast", 10 * MIN, {
    note: `Stir ${yeast} and 1 tsp sugar into 325 ml lukewarm water (about 37 °C - hot water kills the yeast). Wait until it foams; if nothing happens, the yeast is too old.`,
  }),
  card("Mix & knead", 10 * MIN, {
    note: "Pour the yeast water onto 500 g flour (Tipo 00), add 10 g salt and 1 tbsp olive oil (salt not directly onto the yeast). Optional: 1-2 tsp herbes de Provence. Knead until smooth and elastic - it should spring back when pressed.",
  }),
];

export const doughExamples = [
  {
    event: "Pizza (Pizza Stone)",
    description:
      "Four pizzas baked one after another on a hot stone - from fresh dough, cold-proofed dough or dough that's ready.",
    variants: [
      variant("Same-day dough", [
        ...pizzaDough("3 g dry or 10 g fresh yeast"),
        rise(
          "First rise",
          2 * 60 * MIN,
          "Covered, at room temperature, until doubled",
        ),
        card("Divide & shape balls", 10 * MIN, {
          note: "4 balls of about 210 g, tuck the dough underneath until taut",
        }),
        preheatStone(60 * MIN),
        ...bakePizzas(4),
      ]),
      variant("Quick dough", [
        ...pizzaDough("7 g dry or 21 g fresh yeast"),
        rise("First rise", 60 * MIN, "Covered, in a warm place, until doubled"),
        card("Divide & shape balls", 10 * MIN, {
          note: "4 balls of about 210 g, tuck the dough underneath until taut",
        }),
        preheatStone(45 * MIN),
        ...bakePizzas(4),
      ]),
      variant("From the fridge", [
        rise(
          "Dough warms up",
          60 * MIN,
          "Take the cold-proofed balls out of the fridge, keep them covered",
        ),
        preheatStone(60 * MIN),
        ...bakePizzas(4),
      ]),
      variant("Bake only, 4 pizzas", [
        card("Preheat the stone", 60 * MIN, {
          type: "break",
          note: "Stone on the middle rack, oven at its hottest (250-300 °C)",
        }),
        ...bakePizzas(4),
      ]),
      variant("Bake only, 6 pizzas", [
        card("Preheat the stone", 60 * MIN, {
          type: "break",
          note: "Stone on the middle rack, oven at its hottest (250-300 °C)",
        }),
        ...bakePizzas(6),
      ]),
    ],
  },
  {
    event: "Focaccia",
    description:
      "A soft, airy flatbread in a tray, dimpled and drizzled with olive oil.",
    variants: [
      variant("Tray", [
        knead(
          "500 g flour, 400 ml lukewarm water, 10 g salt, 7 g dry yeast, 2 tbsp olive oil - the dough stays sticky",
        ),
        rise("First rise", 90 * MIN, "Covered, until doubled and bubbly"),
        card("Into the oiled tray", 5 * MIN, {
          note: "Gently stretch towards the corners",
        }),
        rise(
          "Second rise - preheat oven now",
          45 * MIN,
          "Oven at 230 °C top/bottom heat",
        ),
        card("Dimple & top", 5 * MIN, {
          note: "Oily fingers deep into the dough, olive oil, flaky salt, rosemary",
        }),
        card("Bake", 22 * MIN, { note: "Until golden brown" }),
        rise("Cool on a rack", 10 * MIN, "So the bottom stays crisp"),
      ]),
    ],
  },
  {
    event: "Bread Rolls",
    description:
      "Crisp rolls - a pizza stone and a little steam make the crust.",
    variants: [
      variant("10 rolls", [
        knead(
          "500 g flour (type 550), 300 ml lukewarm water, 10 g salt, 7 g dry yeast, 1 tsp sugar",
        ),
        rise("First rise", 60 * MIN, "Covered, until doubled"),
        card("Shape rolls", 10 * MIN, {
          note: "10 pieces, shape into tight balls, score the tops",
        }),
        rise(
          "Proof - preheat oven now",
          45 * MIN,
          "Stone or tray in the oven at 230 °C; a heatproof dish on the bottom for water",
        ),
        card("Bake", 20 * MIN, {
          note: "Pour a cup of water into the dish for steam, close the door quickly",
        }),
        rise("Cool on a rack", 10 * MIN),
      ]),
    ],
  },
  {
    event: "Braided Loaf (Hefezopf)",
    description: "A sweet braided yeast loaf - classic for breakfast.",
    variants: [
      variant("1 loaf", [
        knead(
          "500 g flour, 250 ml lukewarm milk, 80 g sugar, 80 g soft butter, 1 egg, 1 pinch of salt, 7 g dry yeast",
        ),
        rise("First rise", 60 * MIN, "Covered, in a warm place, until doubled"),
        card("Shape & braid", 15 * MIN, {
          note: "Three strands, braid from the middle out, onto a lined tray",
        }),
        rise(
          "Second rise - preheat oven now",
          30 * MIN,
          "Oven at 180 °C top/bottom heat",
        ),
        card("Glaze & bake", 35 * MIN, {
          note: "Brush with egg yolk and milk, sprinkle pearl sugar. Cover with foil if it browns too fast.",
        }),
        rise("Cool", 30 * MIN, "On a rack, before slicing"),
      ]),
    ],
  },
  {
    event: "Cinnamon Rolls",
    description: "Soft rolls with a cinnamon-sugar swirl and a glaze.",
    variants: [
      variant("12 rolls", [
        knead(
          "500 g flour, 250 ml lukewarm milk, 60 g sugar, 80 g soft butter, 1 egg, 1 pinch of salt, 7 g dry yeast",
        ),
        rise("First rise", 60 * MIN, "Covered, in a warm place, until doubled"),
        card("Roll, fill & cut", 15 * MIN, {
          note: "Roll out, spread with 80 g butter, 100 g brown sugar, 2 tbsp cinnamon; roll up, cut 12 slices",
        }),
        rise(
          "Second rise - preheat oven now",
          30 * MIN,
          "Into the tin, covered; oven at 180 °C top/bottom heat",
        ),
        card("Bake", 22 * MIN, { note: "Until lightly golden" }),
        rise("Cool a little", 10 * MIN),
        card("Glaze", 5 * MIN, {
          note: "Icing sugar with a little milk or cream cheese",
        }),
      ]),
    ],
  },
  {
    event: "Yeast Sheet Cake",
    description:
      "A tray of Butterkuchen, Streuselkuchen or plum cake on a yeast base.",
    variants: [
      variant("1 tray", [
        knead(
          "500 g flour, 250 ml lukewarm milk, 75 g sugar, 75 g soft butter, 1 egg, 1 pinch of salt, 7 g dry yeast",
        ),
        rise("First rise", 60 * MIN, "Covered, in a warm place, until doubled"),
        card("Roll out & top", 15 * MIN, {
          note: "Onto a lined tray. Butter flakes and sugar, streusel, or halved plums.",
        }),
        rise(
          "Second rise - preheat oven now",
          20 * MIN,
          "Oven at 180 °C top/bottom heat",
        ),
        card("Bake", 25 * MIN, { note: "Until golden" }),
        rise("Cool", 20 * MIN, "On the tray, then cut into squares"),
      ]),
    ],
  },
];

const meditation = (minutes) => [
  card("Arrive", MIN, { note: "Sit comfortably, close your eyes" }),
  card("Breathe", (minutes - 2) * MIN, {
    note: "Follow your breath; when the mind wanders, come back",
  }),
  card("Return", MIN, { note: "Notice the room, open your eyes" }),
];

export const everydayExamples = [
  {
    event: "Cleaning Sprint",
    description:
      "Race the clock room by room - it's surprising how much fits into a timebox.",
    variants: [
      variant("Quick", [
        card("Tidy up", 5 * MIN, { type: "work" }),
        card("Kitchen", 5 * MIN, { type: "work" }),
        card("Bathroom", 5 * MIN, { type: "work" }),
      ]),
      variant("Thorough", [
        card("Kitchen", 10 * MIN, { type: "work" }),
        card("Bathroom", 8 * MIN, { type: "work" }),
        card("Living room", 7 * MIN, { type: "work" }),
        card("Vacuum", 5 * MIN, { type: "work" }),
      ]),
    ],
  },
  {
    event: "Homework",
    description:
      "Plan, work in focused blocks with a break, and check at the end - for kids and students.",
    variants: [
      variant("Short", [
        card("Plan", 5 * MIN, { note: "What needs doing today?" }),
        card("Task 1", 15 * MIN, { type: "work" }),
        card("Break", 5 * MIN, { type: "break" }),
        card("Task 2", 15 * MIN, { type: "work" }),
        card("Check", 5 * MIN, { note: "Is everything packed for tomorrow?" }),
      ]),
      variant("Long", [
        card("Plan", 5 * MIN, { note: "What needs doing today?" }),
        card("Task 1", 20 * MIN, { type: "work" }),
        card("Break", 5 * MIN, { type: "break" }),
        card("Task 2", 20 * MIN, { type: "work" }),
        card("Check", 10 * MIN, { note: "Is everything packed for tomorrow?" }),
      ]),
    ],
  },
  {
    event: "Morning Routine",
    description: "A calm start to the day without losing track of time.",
    variants: [
      variant("Standard", [
        card("Wake up & drink water", 2 * MIN),
        card("Stretch", 5 * MIN, { type: "workout" }),
        card("Shower", 10 * MIN),
        card("Get dressed", 5 * MIN),
        card("Breakfast", 8 * MIN),
      ]),
    ],
  },
  {
    event: "Meditation",
    description: "Arrive, follow your breath, and gently come back.",
    variants: [
      variant("Short", meditation(5)),
      variant("Medium", meditation(10)),
      variant("Long", meditation(20)),
    ],
  },
  {
    event: "Power Nap",
    description:
      "A short nap to recharge - short enough not to wake up groggy.",
    variants: [
      variant("Short", [card("Nap", 10 * MIN, { type: "break" })]),
      variant("Classic", [
        card("Nap", 20 * MIN, {
          type: "break",
          note: "Turn sound alerts on so the end wakes you",
        }),
      ]),
    ],
  },
];
